import { useSceneBackgroundColor } from "@/components/r3f/utils/scene-background";
import { useCached3DObject } from "@/object-cache";
import {
  selectCloudAndCadAlignReference,
  selectCloudCadAlignmentCloudElevation,
  selectCloudCadAlignmentModelElevation,
  selectModelClippingBoxEnabled,
} from "@/store/modes/cloud-cad-alignment-mode-selectors";
import {
  AlignmentReference,
  setCloudElevationForCloudCadAlignment,
  setModelElevationForCloudCadAlignment,
} from "@/store/modes/cloud-cad-alignment-mode-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { View } from "@faro-lotv/app-component-toolbox";
import {
  IElementGenericPointCloudStream,
  IElementModel3dStream,
} from "@faro-lotv/ielement-types";
import { useCallback, useEffect, type JSX } from "react";
import { Box3 } from "three";
import { useNewPerspectiveCamera } from "../alignment-modes-commons/align-to-cad-utils";
import { CloudElevationScene } from "../alignment-modes-commons/cloud-elevation-scene";
import { ModelElevationScene } from "../alignment-modes-commons/model-elevation-scene";
import { useOverlayElements } from "../overlay-elements-context";

type CloudCadElevationSceneProps = {
  /** The cad model used for alignment */
  activeCad: IElementModel3dStream;

  /** The bounding box of the cad model in world */
  cadBox: Box3;

  /** The point cloud used for alignment */
  selectedCloud: IElementGenericPointCloudStream;
};

/** @returns the scene for setting elevations for the cloud - cad alignment*/
export function CloudCadElevationScene({
  activeCad,
  cadBox,
  selectedCloud,
}: CloudCadElevationSceneProps): JSX.Element | null {
  const { firstScreen, secondScreen } = useOverlayElements();

  const background = useSceneBackgroundColor();

  const cloudCamera = useNewPerspectiveCamera();
  const modelCamera = useNewPerspectiveCamera();

  const cadModelObject = useCached3DObject(activeCad, "CloudCadElevationScene");
  const pointCloud = useCached3DObject(selectedCloud, "CloudCadElevationScene");

  const dispatch = useAppDispatch();

  const isModelClippingBoxEnabled = useAppSelector(
    selectModelClippingBoxEnabled,
  );

  const modelElevation = useAppSelector(selectCloudCadAlignmentModelElevation);

  const cloudElevation = useAppSelector(selectCloudCadAlignmentCloudElevation);

  const updateModelElevation = useCallback(
    (elevation: number) => {
      dispatch(setModelElevationForCloudCadAlignment(elevation));
    },
    [dispatch],
  );

  const updateCloudElevation = useCallback(
    (elevation: number) => {
      dispatch(setCloudElevationForCloudCadAlignment(elevation));
    },
    [dispatch],
  );

  const reference = useAppSelector(selectCloudAndCadAlignReference);

  // make sure default cursor is ued to start
  useEffect(() => {
    if (firstScreen && secondScreen) {
      const oldCursor1 = firstScreen.style.cursor;
      const oldCursor2 = secondScreen.style.cursor;
      firstScreen.style.cursor = "default";
      secondScreen.style.cursor = "default";
      return () => {
        firstScreen.style.cursor = oldCursor1;
        secondScreen.style.cursor = oldCursor2;
      };
    }
  }, [firstScreen, secondScreen]);

  return (
    firstScreen &&
    secondScreen && (
      <>
        <View
          camera={cloudCamera}
          trackingElement={
            reference === AlignmentReference.bimModel
              ? firstScreen
              : secondScreen
          }
          background={background}
          hasSeparateScene
        >
          <CloudElevationScene
            pointCloud={pointCloud}
            cloudElevation={cloudElevation}
            updateCloudElevation={updateCloudElevation}
            camera={cloudCamera}
          />
        </View>
        <View
          camera={modelCamera}
          trackingElement={
            reference === AlignmentReference.bimModel
              ? secondScreen
              : firstScreen
          }
          background={background}
          hasSeparateScene
        >
          <ModelElevationScene
            cadModelObject={cadModelObject}
            cadBox={cadBox}
            elevation={modelElevation}
            updateElevation={updateModelElevation}
            isClippingBoxEnabled={isModelClippingBoxEnabled}
            camera={modelCamera}
          />
        </View>
      </>
    )
  );
}
